import { useDebouncedFn, useMount } from '@studio/react-utils/hooks';
import { Events } from 'nats.ws';
import { useState } from 'react';
import type { ReactElement } from 'react';
import type { Props } from './types.js';
import { Watchdog } from './watchdog.jsx';
import { useProducer } from '#~/depot/hooks.js';
import { nats } from '#~/nats/index.js';

/** `<Network>` component. */
export const Network = ({ children }: Props): ReactElement => {
  const producer = useProducer();
  const [isReady, setReady] = useState(false);

  const handleOnline = useDebouncedFn((isOnline: boolean) => {
    producer((draft) => {
      draft.network.isOnline = isOnline;
    });
  }, 1e3);

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    // Initialize network status.
    producer((draft) => {
      draft.network = {
        isConnected: nats.isConnected,
        isOnline: navigator.onLine
      };
    });

    // Watch the NATS connection
    nats.on('closed', () => {
      producer((draft) => {
        draft.network.isConnected = false;
      });
    });

    nats.on('status', ({ type }) => {
      // NATS is pretty limited in it status reporting.
      if (type === Events.Disconnect || type === Events.Reconnect) {
        producer((draft) => {
          draft.network.isConnected = type === Events.Reconnect;
        });
      }
    });

    // Watch the windows's online status.
    window.addEventListener(
      'offline',
      () => {
        handleOnline(false);
      },
      { signal }
    );

    window.addEventListener(
      'online',
      () => {
        handleOnline(true);
      },
      { signal }
    );

    setReady(true);

    return () => {
      abortController.abort();
    };
  });

  return (
    <>
      {children}
      {!!isReady && <Watchdog />}
    </>
  );
};
